import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import { Router } from "@angular/router";
import { AuthService } from "../auth.service";
import { Comment } from "../model/comments";
import { Ticket } from "../model/ticket";
import { response } from "express";

@Injectable({
  providedIn: "root",
})
export class CommentsService {
  private apiUrl: string;
  private byIdUrl: string;
  private ticketCommentsUrl: string;
  public _currentComments: BehaviorSubject<Comment[]>;
  public selectedComment: BehaviorSubject<Comment | null>;

  constructor(
    private http: HttpClient,
    private env: EnvironmentHelper,
    private router: Router,
    private auth: AuthService
  ) {
    this._currentComments = new BehaviorSubject<Comment[]>([]);
    this.selectedComment = new BehaviorSubject<Comment | null>(null);

    this.apiUrl = `${env.apiBaseUrl}comment/`;
    this.ticketCommentsUrl = `${env.apiBaseUrl}tickets/`;
  }

  public createComment(
    token: string,
    ticketId: number,
    comment: Comment
  ): Observable<Messages> {
    const url = `${this.apiUrl}`;

    return new Observable<Messages>((observer) => {
      this.http
        .post<any>(url, comment, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveComment(response, observer),
          (error) => this.receiveComment(error, observer)
        );
    });
  }

  public updateComment(
    token: string,
    ticketId: number,
    comment: Comment
  ): Observable<Messages> {
    if (!comment.id) {
      return new Observable<Messages>((observer) => {
        observer.next(Messages.from({ comment: ["comment.id.required"] }));
      });
    }

    const url = `${this.apiUrl}by-id/${comment.id}`;

    return new Observable<Messages>((observer) => {
      this.http
        .put<any>(url, comment, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveComment(response, observer),
          (error) => this.receiveComment(error, observer)
        );
    });
  }

  public deleteComment(
    token: string,
    ticketId: number,
    commentId: number
  ): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${commentId}`;
    return new Observable<Messages>((observer) => {
      this.http
        .delete<any>(url, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveComment(response, observer),
          (error) => this.receiveComment(error, observer)
        );
    });
  }

  private receiveComments(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Comment[]>)?.body;
      if (!data) {
        observer.next(Messages.from({ comments: ["comments.unknown"] }));
        return;
      }
      this._currentComments.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "auth",
          "unauthorized.access."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "comments",
          "comments.incomplete"
        )
      );
    }
  }

  private receiveComment(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Comment>)?.body;
      if (!data) {
        observer.next(Messages.from({ comment: ["comment.unknown"] }));
        return;
      }
      this.selectedComment.next(data);
      observer.next(Messages.EMPTY);
    } else if (
      response.status === 400 ||
      response.status === 401 ||
      response.status === 409 ||
      response.status === 500
    ) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "comments",
          "comments.load.error"
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "general",
          "an.unexpected.error.has.occurred."
        )
      );
    }
  }

  public getCommentById(
    token: string,
    commentId: number
  ): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(`${this.byIdUrl}${commentId}`, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveComments(response, observer),
          (error) => this.receiveComments(error, observer)
        );
    });
  }
  public selectFromRouter() {
    const segments = this.getLastSuccessfulNavigationSegments();

    if (segments && segments.length > 0) {
      const commentId = segments[segments.length - 1];
      const token = this.auth.token;

      this.getCommentById(token, Number(commentId)).subscribe();
    } else {
      const emptyComment = new Comment(0, 0, "", "");
      this.select(emptyComment);
    }
  }
  private getLastSuccessfulNavigationSegments(): string[] | null {
    const segments =
      this.router.lastSuccessfulNavigation?.finalUrl?.root.children["primary"]
        .segments;
    return segments ? segments.map((segment) => segment.path) : null;
  }

  public select(comment: Comment) {
    this.selectedComment.next(comment);
  }

  public accept(comments: Comment[]) {
    this._currentComments.next(comments);
  }

  public get selectedCommentObservable(): Observable<Comment | null> {
    return this.selectedComment.asObservable();
  }

  public get commentsObservable(): Observable<Comment[]> {
    return this._currentComments.asObservable();
  }

  public get commentsData(): Comment[] {
    return this._currentComments.value;
  }

  public get currentComment(): Comment {
    const value = this.selectedComment.value;
    return value || Comment.EMPTY;
  }
}
